import {
  listenFeaturedBusinesses,
  type FeaturedBusinessRow,
} from "@/lib/directory/featuredBusinesses";
import { router } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { FlatList, Image, Pressable, Text, View } from "react-native";

type RowProps = {
  item: FeaturedBusinessRow;
};

export default function FeaturedCarouselSmall() {
  const [items, setItems] = useState<FeaturedBusinessRow[]>([]);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const unsub = listenFeaturedBusinesses(
      { limitCount: 20 },
      (rows) => {
        setItems(rows);
        setHasError(false);
      },
      (error) => {
        console.log("FAILED: FeaturedCarouselSmall businesses listener", error);
        setItems([]);
        setHasError(true);
      }
    );

    return () => unsub();
  }, []);

  const data = useMemo(() => items.filter((item) => !!item.slug), [items]);

  function renderRow({ item }: RowProps) {
    return (
      <Pressable
        onPress={() =>
          item.slug &&
          router.push({
            pathname: "/p/[slug]",
            params: { slug: item.slug },
          })
        }
        style={styles.card}
      >
        <Image
          source={{
            uri: item.logoUrl || "https://via.placeholder.com/120x120.png?text=LOGO",
          }}
          style={styles.logo}
        />

        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={styles.name} numberOfLines={1}>
            {item.name || "Business"}
          </Text>

          <Text style={styles.meta} numberOfLines={1}>
            {[item.category, item.cityLine].filter(Boolean).join(" • ")}
          </Text>
        </View>
      </Pressable>
    );
  }

  if (hasError) {
    return (
      <View style={{ paddingVertical: 6 }}>
        <Text style={{ fontSize: 12, fontWeight: "700", color: "#6b7280" }}>
          Could not load featured businesses.
        </Text>
      </View>
    );
  }

  if (!data.length) {
    return (
      <View style={{ paddingVertical: 6 }}>
        <Text style={{ fontSize: 12, fontWeight: "700", color: "#6b7280" }}>
          No featured businesses yet.
        </Text>
      </View>
    );
  }

  return (
    <FlatList
      horizontal
      showsHorizontalScrollIndicator={false}
      data={data}
      keyExtractor={(item) => item.id}
      contentContainerStyle={{ gap: 10, paddingRight: 6 }}
      renderItem={renderRow}
    />
  );
}

const styles = {
  card: {
    width: 260,
    height: 62,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#fff",
    padding: 10,
    flexDirection: "row" as const,
    alignItems: "center" as const,
    gap: 10,
  },
  logo: {
    width: 42,
    height: 42,
    borderRadius: 12,
    backgroundColor: "#e5e7eb",
  },
  name: {
    fontSize: 12,
    fontWeight: "900" as const,
    color: "#111827",
  },
  meta: {
    fontSize: 11,
    fontWeight: "700" as const,
    color: "#6b7280",
    marginTop: 2,
  },
};